import React, { useState, useEffect } from 'react';
import { useBeaconNavigator } from 'src/components/BeaconV2Components/BeaconNavigation/useBeaconNavigator';
import BasePageContainer from 'src/components/CommonV2Components/BasePageContainer/BasePageContainer';
import { BeaconSubtitleContainer } from 'src/components/BeaconV2Components/BeaconChartWithLegend/BeaconSubtitleContainer';
import AdvancedSearchRequestBuilder from 'src/components/BeaconV2Components/utils/AdvancedSearchRequestBuilder';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import {
  AdClickField,
  AdSpendField,
  AdSalesField,
  ROASField
} from 'src/components/BeaconV2Components/ExperimentalLayout/Advertising/AdMetric/AdMetricGrid/constants';
import { FluentSearchFilterBuilder } from 'src/components/BeaconV2Components/ExperimentalLayout/Advertising/Rules/RulesDetails/SearchBuilder';
import { SearchTextField } from 'src/components/BeaconV2Components/TableSearch';
import MetricListContainer from 'src/components/BeaconV2Components/MetricList/MetricListContainer';
import { SlButton } from 'src/components/BeaconV2Components/Header/SLDropdownMenu/SlButton';
import {
  CampaignColumnDefinition,
  CampaignTypeField,
  CampaignAutomationField,
  PortfolioCampaignCheckBox
} from 'src/components/BeaconV2Components/MetricList/headColumnDefinitions';
import { GetAdMetricIndexName } from 'src/components/BeaconV2Components/ExperimentalLayout/Advertising/AdMetric/AdMetricsUtils';
import { useDebouncedValue, useAppSelector } from 'src/utils/Hooks';
import { useMainEntityType } from 'src/utils/Hooks/reduxSelectors';
import AggregationBuilder from 'src/components/BeaconV2Components/utils/AggregationBuilder';
import { campaignSearchTermFilters } from 'src/components/BeaconV2Components/ExperimentalLayout/Advertising/Rules/RulesDetails/utils';
import PortfolioStore from './stores';

const PortfolioSettings = () => {
  const entityType = useMainEntityType();
  const { startDayId, endDayId } = useAppSelector((state) => state.mainTimePeriod);
  const filterMap = {
    adCampaign: campaignSearchTermFilters
  };
  const { beaconNavigator } = useBeaconNavigator();
  const { 
    selectedCampaignIds, 
    portfolioName, 
    setCampaignIds, 
    setCampaignData,
    searchTerm,
    setSearchTerm 
  } = PortfolioStore((s) => ({
    setCampaignIds: s.setCampaignIds,
    setCampaignData: s.setCampaignData,
    campaignIds: s.campaignIds,
    portfolioName: s.portfolioName,
    selectedCampaignIds: s.selectedCampaignIds,
    searchTerm: s.searchTerm,
    setSearchTerm: s.setSearchTerm
  }));
  const debouncedSearchValue = useDebouncedValue(searchTerm);

  const searchBuilder = new FluentSearchFilterBuilder(filterMap);

  useEffect(() => {
    if (!portfolioName) {
      beaconNavigator.updateSearchParams({ tab: 'advertising', subtab: 'portfolioSettings' });
      beaconNavigator.go();
    }
  }, [beaconNavigator, portfolioName]);

  return (
    <BasePageContainer>
      <Text variant="h4">Assign Campaigns</Text>
      <BeaconSubtitleContainer>
        <Text variant="body1">
          Select the campaigns you would like to assign to this portfolio. This step is optional.
        </Text>
      </BeaconSubtitleContainer>
      <div
        style={{
          marginBottom: '32px'
        }}
      >
        <SearchTextField
          placeholder="Search campaigns"
          onChange={(e) => {
            e.preventDefault();
            setSearchTerm(e.target.value);
          }}
          value={searchTerm}
        />
      </div>

      <MetricListContainer
        skeletonRows={20}
        showHeader={false}
        headColumnDefinition={[
          PortfolioCampaignCheckBox,
          CampaignColumnDefinition,
          CampaignTypeField,
          CampaignAutomationField
        ]}
        entityTableHeaderProps={{
          prefixTitle: 'Ad Metric by',
          title: ''
        }}
        indexName={GetAdMetricIndexName(entityType, 'adCampaign')}
        fields={[AdClickField, AdSpendField, AdSalesField, ROASField]}
        useTableDataProps={{
          buildRequest: (builder: AdvancedSearchRequestBuilder) => {
            builder
              .removeConditionTermFilters((builderFilter) => builderFilter.fieldName === 'categoryId')
              .removeConditionRangeFilters((builderFilter) => builderFilter.fieldName === 'weekId')
              .addConditionTermFilter('isProjected', 'must', [0])
              .addConditionRangeFilter('dayId', startDayId, endDayId);

            if (debouncedSearchValue !== '') {
              builder.addNestedFilterCondition('should', searchBuilder.setSearchTerm(debouncedSearchValue).build());
            }

            return builder;
          },
          buildAggregation: (builder: AggregationBuilder) => {
            return builder
              .addConditionRangeFilter('dayId', startDayId, endDayId)
              .removeConditionTermFilter('weekId')
              .addConditionTermFilter('isProjected', ['0'], 'must')
              .removeConditionRangeFilters('weekId')
              .clearComparisonRangeFilters()
              .addComparisonRangeFilter('dayId', startDayId, endDayId);
          },
          groupByFieldName: 'campaignId',
          filterResponse: () => true,
          itemsPerPage: 1000
        }}
        sortFieldName="clicks"
        showGrid={false}
        groupBy="stacklineSku"
        exposeTableData={(data) => {
          if (data.length > 0) {
            // Store campaign IDs
            const campaigns = data.reduce((acc: Set<string>, item) => {
              acc.add(item.campaignId);
              return acc;
            }, new Set<string>());
            
            setCampaignIds(campaigns);
            
            // Store campaign data including names for searching
            const campaignDataMap = data.reduce((acc, item) => {
              acc[item.campaignId] = {
                campaignId: item.campaignId,
                campaignName: item.campaign?.campaignName || item.campaignName || ''
              };
              return acc;
            }, {});
            
            setCampaignData(campaignDataMap);
          }
        }}
      />
      <div
        style={{
          marginLeft: 'auto',
          width: '100%',
          display: 'flex',
          justifyContent: 'space-between',
          marginTop: '64px'
        }}
      >
        <SlButton
          buttonSx={{
            bacgrkoundColor: 'white'
          }}
          onClick={() => {
            beaconNavigator.back();
          }}
        >
          Back
        </SlButton>
        <SlButton
          buttonSx={{
            bacgrkoundColor: 'white',
            marginLeft: 'auto',
            marginRight: '16px'
          }}
          disabled={selectedCampaignIds.size !== 0}
          onClick={() => {
            beaconNavigator.updateSearchParams({ tab: 'advertising', subtab: 'portfolioConfirmation' });
            beaconNavigator.go();
          }}
        >
          Skip
        </SlButton>

        <SlButton
          variant="contained"
          disabled={selectedCampaignIds.size === 0}
          onClick={() => {
            beaconNavigator.updateSearchParams({ tab: 'advertising', subtab: 'portfolioConfirmation' });
            beaconNavigator.go();
          }}
        >
          Next
        </SlButton>
      </div>
    </BasePageContainer>
  );
};

export default PortfolioSettings;
